import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    useMemo,
} from 'react';

const AppVersionContext = createContext({});

export const useAppVersion = () => {
    const context = useContext(AppVersionContext);

    if (!context) {
        throw new Error('useAppVersion must be used within AppVersionProvider');
    }

    return context;
};

export const AppVersionProvider = ({ children }) => {
    const [showUpdateDialog, setShowUpdateDialog] = useState(false);
    const [appVersionInfo, setAppVersionInfo] = useState(null);

    const initialVersionRef = useRef(null);
    const appVersionInfoKeyRef = useRef(null);

    const checkAppVersionString = useCallback((appVersion) => {
        if (!appVersion) return;

        if (!initialVersionRef.current) {
            initialVersionRef.current = appVersion;
            return;
        }

        if (appVersion === initialVersionRef.current) {
            return;
        }

        const updatedAtKey = 'updated-to-appVersion-' + appVersion;
        const updatedAt = localStorage.getItem(updatedAtKey);

        if (
            updatedAt &&
            new Date(updatedAt) > new Date(Date.now() - 1000 * 60 * 60)
        ) {
            return;
        }

        localStorage.setItem(updatedAtKey, new Date().toISOString());
        setShowUpdateDialog(true);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchVersion = async () => {
            try {
                const response = await fetch('/version.json', {
                    cache: 'no-store',
                });

                if (!response.ok) return;

                const raw = (await response.text())?.trim();

                if (!raw) return;

                let versionStr = null;
                let versionMeta = null;

                try {
                    const parsed = JSON.parse(raw);

                    if (parsed && parsed.version != null) {
                        const version = String(parsed.version);
                        const buildNumber =
                            parsed.buildNumber != null
                                ? Number(parsed.buildNumber)
                                : null;
                        const commit = parsed.commit || null;

                        versionMeta = {
                            version,
                            buildNumber,
                            commit,
                        };

                        versionStr =
                            buildNumber != null
                                ? `${version}-${buildNumber}`
                                : version;
                    }
                } catch {
                    versionStr = raw;
                }

                if (cancelled) return;

                if (versionMeta) {
                    const versionMetaKey = JSON.stringify(versionMeta);

                    if (versionMetaKey !== appVersionInfoKeyRef.current) {
                        appVersionInfoKeyRef.current = versionMetaKey;
                        setAppVersionInfo(versionMeta);
                    }
                }

                if (versionStr) {
                    checkAppVersionString(versionStr);
                }
            } catch (e) {
                // ignore network errors
            }
        };

        fetchVersion();

        const intervalId = setInterval(fetchVersion, 35 * 1000);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [checkAppVersionString]);

    const handleReload = useCallback(() => {
        window.location.reload();
    }, []);

    const value = useMemo(
        () => ({
            showUpdateDialog,
            setShowUpdateDialog,
            handleReload,
            appVersionInfo,
        }),
        [showUpdateDialog, handleReload, appVersionInfo],
    );

    return (
        <AppVersionContext.Provider value={value}>
            {children}
        </AppVersionContext.Provider>
    );
};